import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getOfficeEducation } from '~/actions';
import { useUserSelector } from '~/hooks/useUserSelector';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import type { ISignUpFormValues } from '../types';

interface IUserLike {
  contact_number?: string | null;
  office_name?: string | null;
  office_info?: { officeType?: string | null; officeSpecialty?: string | null } | null;
  address?: Array<{ formatted_address?: string | null }> | null;
}

interface IUserCredentialsResponse {
  education?: {
    specialtiesAndProcedures?: Record<string, string[]>;
  };
}

export const useOfficeDetailsHydration = () => {
  const dispatch = useDispatch();
  const user = useUserSelector() as IUserLike | null | undefined;
  const { setValue } = useFormContext<ISignUpFormValues>();

  useEffect(() => {
    // OfficeDetailsPage's `useRequireSignUpStep` guard fires first and will
    // redirect when the user is missing or short-circuits to an earlier
    // step — bail out so we don't hit the API for a doomed render.
    if (!user || !user.contact_number?.trim()) return;

    dispatch(getOfficeEducation());

    if (user.office_name) setValue('officeName', user.office_name);
    const savedAddress = user.address?.[0]?.formatted_address;
    if (savedAddress) setValue('officeAddress', savedAddress);
    if (user.office_info?.officeType) setValue('officeType', user.office_info.officeType);

    // The user's specialty lives on /credential-service/user/credentials
    // (not the catalog endpoint). The in-session fallback below covers the
    // case where this fetch is still in flight.
    apiMethods
      .userCredentialsRequest()
      .then((data: IUserCredentialsResponse | undefined) => {
        dispatch({ type: apiMethodsConst.USER_CREDENTIALS_REQUEST, payload: data });
        const saved = Object.keys(data?.education?.specialtiesAndProcedures ?? {})[0];
        if (saved) setValue('officeSpecialty', saved);
      })
      .catch(() => {
        // Non-blocking — user can re-pick.
      });

    if (user.office_info?.officeSpecialty) {
      setValue('officeSpecialty', user.office_info.officeSpecialty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
